import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { KeycloakAuthProvider } from '@it-sce/ui/contexts/KeycloakAuthContext'
import App from './App'
import { installGlobalErrorHandlers } from './lib/errorTelemetry'
import { authApiBaseUrl, loadRuntimeConfig, type RuntimeConfig } from './lib/runtimeConfig'
import { RealmHostAPI } from './services/realmHostApi'
import { CortexHostAPI } from './services/cortexHostApi'
import { api } from './services/api'
import { authStorageProps, scopedSessionStorage } from './lib/storage'
import { idpHintFromSearch } from './lib/idpHint'
import './index.css'

// TQ #8133: Globale Error-Telemetrie SO FRUEH WIE MOEGLICH registrieren —
// noch vor createRoot(). So werden auch synchrone Bootstrap-Fehler und
// unhandled Promise-Rejections (die eine React-ErrorBoundary NICHT faengt)
// erfasst und unter dem Marker [white-screen-guard] geloggt/gemeldet.
installGlobalErrorHandlers()

// TQ #409 (SPEC-Cockpit-Pro-Kunde §4.1): Realm, Keycloak, API, Modus und Branding
// kommen aus /config.json (lib/runtimeConfig.ts), nicht mehr aus einer Host-Map.
// Die Config wird VOR dem Keycloak-Init geladen; Root rendert erst danach.
//
// PFLICHT (memory feedback_cockpit-realm-per-domain): Der Realm ist deterministisch
// und nicht ueber Build-Konfig veraenderbar — KEINE Cross-Tenant-Logins ueber
// IT-SCE-Workspace. Fehlt `realm` in der Config (geteiltes Cockpit auf einer
// Tenant-Domain), loest wie bisher /api/public/realm-for-host auf
// (organizations.custom_domain, TQ #9707/#9813). Gibt es weder Config-Realm
// noch Lookup-Treffer, erscheint eine Fehlermeldung — NIE ein Fallback auf
// einen anderen Realm.
async function resolveRealm(cfg: RuntimeConfig): Promise<string> {
  if (cfg.realm) return cfg.realm
  return RealmHostAPI.resolve(window.location.hostname)
}

// idpHint-Resolve in 2 Stufen:
//   1. URL-Param (?idp=... oder ?kc_idp_hint=...) — Magic-Link / Support-Login
//   2. sessionStorage (Persistenz nach Redirect/Reload) — HOST-GESCOPED!
//   3. ENV-Default (fallback)
//
// TQ #8096: Der persistierte Hint MUSS pro Host gescoped sein. Frueher lag er
// host-unabhaengig unter 'cockpit_idp_hint' — wer zuerst ein Tenant-Cockpit
// (cockpit.eza-… → hint='eza') oder einen ?idp=-Link besuchte, schleppte den
// Hint mit auf cockpit.it-sce.de (Realm IT-SCE, 0 IdPs). org-portal schickte
// dann kc_idp_hint=eza → Keycloak 'Provider not found for realm it-sce' →
// Email-Maske re-render = Login-Loop. Host-Scoping behebt das strukturell:
// ein auf Host A gesetzter Hint ist auf Host B unsichtbar.
//
// TQ #9673: Die fruehere Stufe 1 (Hostname → hostMap → kc_idp_hint=eza/bib/
// glasbrenner/bum) entfernt. Sie war eine Altlast aus einer frueheren
// Broker-Architektur (Custom-Domain → IT-SCE-Workspace → IdP-Broker <tenant>).
// Seit dem Wechsel auf direkten Realm-Login (resolveRealm() geht bereits
// direkt gegen EZA/bib/glasbrenner/bum) existieren in diesen Realms KEINE
// IdPs mehr (KC Admin-API verifiziert: identity-provider/instances == [] fuer
// alle 4). kc_idp_hint=eza gegen Realm EZA lief daher ins Leere — Keycloak
// ignoriert einen unbekannten IdP-Alias und rendert die normale Login-Form
// (headless verifiziert per PKCE-Auth-Request: HTTP 200, "Sign in to EZA",
// kein Fehler/Loop). Kein aktiver Bug, aber tote/irrefuehrende Konfiguration
// — entfernt statt behalten.

/** Per-Host sessionStorage-Key fuer den IdP-Hint (TQ #8096). */
function idpHintStorageKey(host: string): string {
  return `cockpit_idp_hint:${host}`
}

function resolveIdpHint(): string | undefined {
  const host = window.location.hostname
  const storageKey = idpHintStorageKey(host)

  // Migration/Cleanup: den alten host-unabhaengigen Key entfernen, damit ein
  // vor diesem Fix geleakter Hint nicht weiter den Loop ausloest (TQ #8096).
  scopedSessionStorage.removeItem('cockpit_idp_hint')

  // 1) URL-Param (?idp= oder ?kc_idp_hint=, siehe lib/idpHint.ts)
  const fromQuery = idpHintFromSearch(window.location.search)
  if (fromQuery) {
    scopedSessionStorage.setItem(storageKey, fromQuery)
    return fromQuery
  }

  // 2) Persistierter Wert (NUR der fuer diesen Host gesetzte)
  const stored = scopedSessionStorage.getItem(storageKey)
  if (stored) return stored

  // 3) Default
  return undefined
}

// TQ #409: Root laedt zuerst die Runtime-Config, loest dann den Realm auf und
// mountet erst danach den KeycloakAuthProvider (Loading-State dazwischen).
// idpHint hat keine Host->Realm-Abhaengigkeit mehr (TQ #9673) und bleibt
// synchron, einmalig per Lazy-State berechnet (Seiteneffekt: schreibt sessionStorage).
function Root() {
  const [boot, setBoot] = useState<{ cfg: RuntimeConfig; realm: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [idpHint] = useState<string | undefined>(() => resolveIdpHint())

  useEffect(() => {
    let cancelled = false
    loadRuntimeConfig()
      .then(async cfg => {
        const realm = await resolveRealm(cfg)
        api.setBaseURL(cfg.apiBase)
        // Cortex-Origin: Kunden-Instanz/Config gewinnt. Nur ohne cortexUrl (geteiltes
        // Cockpit) loest die zentrale API per cortex-for-host auf (nicht blockierend,
        // Glasbrenner-Cockpit-Fix 23.09.2026; faellt fail-safe auf die geteilte Instanz).
        if (cfg.cortexUrl) api.setCortexBaseURL(cfg.cortexUrl)
        else void CortexHostAPI.resolve(window.location.hostname).then(url => api.setCortexBaseURL(url))
        if (cfg.primaryColor) document.documentElement.style.setProperty('--brand-primary', cfg.primaryColor)
        document.title = `${cfg.customerLabel} Cockpit`
        if (!cancelled) setBoot({ cfg, realm })
      })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)) })
    return () => { cancelled = true }
  }, [])

  if (error) {
    // Config fehlt/ungueltig oder Realm nicht aufloesbar — kein White-Screen,
    // sondern eine erklaerende Meldung.
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
        Cockpit konnte nicht geladen werden: {error}
      </div>
    )
  }
  if (!boot) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Lade…</div>
  }
  const { cfg, realm } = boot

  return (
    <KeycloakAuthProvider
      keycloakUrl={cfg.keycloakUrl}
      realm={realm}
      clientId={cfg.clientId}
      idpHint={idpHint}
      // TQ #5350: Tenant-Cockpit (mode=tenant, eigene Domain wie
      // cockpit.eza-wolpertshausen.de) braucht login-required statt check-sso.
      // Silent SSO via Iframe schlaegt auf Cross-Domain fehl (3rd-party cookies /
      // SameSite), wodurch der User post-Redirect ohne Token bei /<token> landet
      // und 'Organisation nicht gefunden' sieht. login-required triggert stattdessen
      // einen vollen OIDC-Redirect; nach Auth kommt der User zurueck zur gleichen
      // URL mit code/state und kc.init() processiert sie.
      // Voraussetzung (vorhanden): redirectUris im 'organization-portal-frontend'
      // KC-Client enthalten alle custom-domain-Patterns.
      // TQ #6906: mode=personal und mode=shared -> check-sso, damit Pre-Login
      // ScoutLandingPage rendert bzw. der Org-Picker greift.
      onLoad={cfg.mode === 'tenant' ? 'login-required' : 'check-sso'}
      enableLogging={true}
      apiBaseUrl={authApiBaseUrl(cfg)}
      portalName="Organization Portal"
      // TQ #409 (§4.1 a/b): Pfad-Instanz -> Praefix <kennung>:, kein Token in localStorage,
      // Logout zurueck auf /<kennung>/. Legacy unveraendert.
      {...authStorageProps()}
    >
      <App />
    </KeycloakAuthProvider>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
)
